<template>
  <div class="action-list" :class="`${row2n % 2 ? 'row2n' : 'row2np1'}`">
    <div v-if="actions.length === 0" class="empty-actions">
      {{ t("modules.sqdc.components.eventActionBoard.modals.SQDCActionsField.noActions") }}
    </div>
    <component
      :is="componentForAction(action)"
      v-for="(action, index) in activeActions"
      :key="`${action.id}_${action.__typename}`"
      :action="action"
      :event="event"
      :archived="eventArchived"
      :class="getActionClass(index, row2n)"
      @open:ih-order-modal="$emit('open:ihOrderModal', $event)"
    />
    <div
      v-if="inactiveActions > 0"
      class="inactive-actions cursor-pointer"
      @click="showInactive = true"
    >
      {{ t("components.eventAction.action.ActionList.inactiveActions") }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { useTranslation } from "@/language/translation";

import { computed, inject, type PropType, ref, provide } from "vue";
import { DateTime } from "luxon";
import {
  type ActionListActionFieldsFragment,
  type EventListEventFieldsFragment,
  TaskTyp,
} from "@/graphql";
import IHOrderAction from "@/modules/ih/components/IHOrderAction.vue";
import { ActionFilterInjectionKey } from "../filter/FilterTypes";
import { isActionActive } from "../filter/Filter";
import { BadgeColorInjectionKey } from "../issue/IssueTypes";
import PSC from "../psc/PSC.vue";
import CommonTask from "../task/CommonTask.vue";
import AuditTask from "../task/AuditTask.vue";
import IdeaManageTask from "@/modules/idea/components/eab/IdeaManageTask.vue";
import IdeaIdeaTask from "@/modules/idea/components/eab/IdeaIdeaTask.vue";

const props = defineProps({
  actions: {
    type: Array as PropType<ActionListActionFieldsFragment[]>,
    required: true,
  },
  event: {
    type: Object as PropType<EventListEventFieldsFragment>,
    required: true,
  },
  /** defines the starting background color (striped) of the action list */
  row2n: {
    type: Number,
    required: false,
    default: 0,
  },
});
defineEmits(["open:ihOrderModal"]);
provide(BadgeColorInjectionKey, "");
const showInactive = ref(false);

const actionFilterList = inject(
  ActionFilterInjectionKey,
  ref({ updatedAt: DateTime.now(), actionFilters: [] }),
);

const activeActions = computed(() => {
  if (showInactive.value) {
    return props.actions;
  }
  return props.actions.filter((action) => {
    return isActionActive(actionFilterList.value, action);
  });
});

const inactiveActions = computed(() => {
  return props.actions.length - activeActions.value.length;
});

function componentForAction(action: ActionListActionFieldsFragment & { __typename: string }): any {
  switch (action.__typename) {
    case "Task":
      switch (action.type) {
        case TaskTyp.ideaIdea:
          return IdeaIdeaTask;
        case TaskTyp.ideaManage:
          return IdeaManageTask;
        case TaskTyp.common:
        case TaskTyp.CUSTOM:
        case TaskTyp.FORWARDED_ORIGIN:
        case TaskTyp.FORWARDED_COPY:
        case TaskTyp.FEEDBACK_ORIGIN:
        case TaskTyp.FEEDBACK_COPY:
          return CommonTask;
        case TaskTyp.AUDIT_TASK:
          return AuditTask;
        default:
          throw Error("task type is not implemented");
      }
    case "PSC":
      return PSC;
    case "IHOrder":
      return IHOrderAction;
    default:
      // @ts-expect-error default case for new actions. Type is never because the new action does not exists yet.
      throw Error(`unknown action type ${action.__typename}`);
  }
}
const eventArchived = computed(() => props.event.deletedAt != null);
function getActionClass(index: number, row2n: number) {
  return {
    row2n: (index + row2n) % 2,
    row2np1: !((index + row2n) % 2),
    borderTop: index !== 0,
  };
}

const { t } = useTranslation();
</script>

<style lang="scss" scoped>
.action-list {
  border: 1px solid var(--p-gray-900);
  border-left: none;
  border-radius: 3px;

  .borderTop {
    border-top: 1px solid lightgray;
  }

  .action:first-of-type {
    border-top: 0;
  }

  .inactive-actions {
    padding: 0.5em;
    text-align: center;
  }

  .empty-actions {
    padding: 0.75rem;
    color: var(--p-text-muted-color);
    text-align: center;
  }
}
</style>
